import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Phone, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/Reveal";
import { site, homeVideo } from "@/lib/siteConfig";
import { commonContent } from "@/cms/cmsModel";

// Requested hero typography. The licensed faces fall back to the site's premium
// serif stack (already loaded) so the look stays consistent and translation-safe.
const HEAD_FONT = '"Apple Garamond BT", "EB Garamond", Georgia, "Times New Roman", serif';
const BODY_FONT = '"Minion Variable Concept", "Crimson Pro", Georgia, serif';

// Full-width cinematic video hero. The existing project video plays directly
// below the header, autoplay + muted + loop + playsInline, with a poster while
// it loads. A single video means no slider arrows / pagination dots.
export const HeroVideo = () => {
  const copy = commonContent.hero;
  const videoRef = useRef(null);
  const [minHeight, setMinHeight] = useState("max(540px, calc(100vh - 110px))");

  // Measure the header stack (desktop TopBar in flow + sticky Navbar) so the
  // hero fills exactly the rest of the viewport: calc(100vh - header height).
  useLayoutEffect(() => {
    const measure = () => {
      const header = document.querySelector("header");
      let h = 0;
      if (header) {
        h += header.offsetHeight;
        const topbar = header.previousElementSibling;
        if (topbar) h += topbar.offsetHeight; // 0 when hidden on mobile
      }
      setMinHeight(`max(540px, calc(100vh - ${h}px))`);
    };
    measure();
    window.addEventListener("resize", measure);
    const t = setTimeout(measure, 400); // re-measure after fonts/layout settle
    return () => {
      window.removeEventListener("resize", measure);
      clearTimeout(t);
    };
  }, []);

  // Some browsers need the muted DOM property explicitly set for autoplay.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    const p = v.play();
    if (p && typeof p.catch === "function") p.catch(() => {});
  }, []);

  return (
    <section
      className="relative w-full overflow-hidden bg-[#0e1216]"
      style={{ minHeight }}
      data-testid="hero-video-section"
      aria-label={`${site.name} – ${site.slogan}`}
    >
      <video
        ref={videoRef}
        className="az-hero-video absolute inset-0 h-full w-full object-cover [object-position:50%_28%] sm:[object-position:center]"
        src={homeVideo.src}
        poster={homeVideo.poster}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        aria-hidden="true"
        tabIndex={-1}
      />

      {/* Readability gradients: stronger at the bottom + left for lower-left text */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "linear-gradient(to top, rgba(12,16,20,0.9) 0%, rgba(12,16,20,0.5) 36%, rgba(12,16,20,0.22) 60%, rgba(12,16,20,0.45) 100%)",
        }}
      />
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "linear-gradient(to right, rgba(12,16,20,0.72) 0%, rgba(12,16,20,0.26) 46%, transparent 72%)",
        }}
      />

      {/* Content – elegantly placed in the lower-left */}
      <div className="absolute inset-0 z-10 flex items-end">
        <div className="az-container w-full pb-14 pt-28 sm:pb-20 lg:pb-24">
          <div className="max-w-2xl text-[#F1F4F8]">
            <Reveal>
              <span className="az-eyebrow" style={{ color: "#C99A3D" }}>
                <span className="inline-block h-px w-7 bg-current" />
                {site.name} · {site.regionShort}
              </span>
            </Reveal>
            <Reveal delay={0.06} duration={1.0}>
              <h1
                className="mt-4 text-5xl font-bold leading-[1.05] tracking-[-0.02em] sm:text-6xl lg:text-7xl"
                style={{ fontFamily: HEAD_FONT }}
                data-testid="hero-headline"
              >
                {copy.headline}
              </h1>
            </Reveal>
            <Reveal delay={0.15} duration={1.1}>
              <p
                className="mt-5 max-w-xl text-lg leading-relaxed text-[rgba(244,246,249,0.88)] sm:text-xl"
                style={{ fontFamily: BODY_FONT }}
                data-testid="hero-subheadline"
              >
                {copy.subtitle}
              </p>
            </Reveal>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <Reveal delay={0.2} duration={0.8}>
                <a href={`tel:${site.primaryPhoneTel}`} data-testid="hero-hotline-button">
                  <Button className="w-full rounded-xl bg-[#7A1516] px-6 py-6 text-base text-[#F1F4F8] hover:bg-[#941d1e] sm:w-auto">
                    <Phone size={18} className="mr-2" /> {copy.phoneButton}
                  </Button>
                </a>
              </Reveal>
              <Reveal delay={0.32} duration={0.8}>
                <Link to="/kontakt" data-testid="hero-contact-button">
                  <Button
                    variant="outline"
                    className="w-full rounded-xl border-[rgba(244,246,249,0.5)] bg-[rgba(244,246,249,0.08)] px-6 py-6 text-base text-[#F1F4F8] backdrop-blur-sm hover:bg-[rgba(244,246,249,0.16)] hover:text-[#F1F4F8] sm:w-auto"
                  >
                    {copy.contactButton} <ArrowRight size={18} className="ml-2" />
                  </Button>
                </Link>
              </Reveal>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
